import { CampaignPhase, OfferType, SubType } from '@shared/constants';
import {
  ChallengeBaseDTO,
  FrequencyChallengeDTO,
  ReferralChallengeDTO,
  SelectToWinChallengeDTO,
  StepChallengeDTO,
} from '@shared/dto';

import { ProductScope, Step } from './product.factory';

// Restricted to the OfferType values that have a corresponding subclass below.
// Adding a new OfferType without a matching subclass will fail this assignment
// in the ChallengeBase constructor.
type KnownOfferType =
  | OfferType.STEP
  | OfferType.FREQUENCY
  | OfferType.SPONSOR
  | OfferType.SELECT_TO_WIN;

export class ChallengeBase {
  public campaignId: number;
  public offerId: number;
  public rank: number;
  public type: KnownOfferType;
  public subType: SubType;
  public reward = 0;
  public progress = 0;
  public selectedDate: string = '';
  public remainingSelections = 1000;

  constructor(campaignId: number, challengeDTO: ChallengeBaseDTO) {
    this.campaignId = campaignId;
    this.rank = challengeDTO.rank;
    this.type = challengeDTO.type;
    this.reward = challengeDTO.reward;
    this.offerId = challengeDTO.offerId;
    this.subType = challengeDTO.sub_type;
    this.progress = challengeDTO.progress;
    this.selectedDate = challengeDTO.selected_date;
    this.remainingSelections =
      challengeDTO.remaining_selections ?? this.remainingSelections;
  }

  get isSelected(): boolean {
    return !!this.selectedDate;
  }

  getChallengeName(): string {
    return this.type;
  }

  isOutOfStock(): boolean {
    return !this.isSelected && this.remainingSelections <= 0;
  }

  // Unselecting is only offered while nothing has been earned yet on a
  // campaign that is still running.
  canBeUnselected(phase: CampaignPhase): boolean {
    return (
      this.isSelected &&
      this.progress === 0 &&
      phase !== CampaignPhase.COMPLETED
    );
  }

  isInStock(): boolean {
    // A challenge is in stock if it's not selected and has remaining selections
    return !this.isSelected && this.remainingSelections > 0;
  }
}

export class StepChallenge extends ChallengeBase {
  declare public type: OfferType.STEP;
  public steps: Step[];
  public driveLink: string;
  public productScope: ProductScope;

  constructor(campaignId: number, challengeDTO: StepChallengeDTO) {
    super(campaignId, challengeDTO);
    this.steps = challengeDTO.steps;
    this.driveLink = challengeDTO.drive_link;
    this.productScope = new ProductScope(challengeDTO.product_scope);
  }

  get targets(): number[] {
    return this.steps.map((step: Step) => step.target);
  }

  get isCompleted(): boolean {
    return (
      this.isSelected && this.progress >= this.targets[this.steps.length - 1]
    );
  }

  hasBanner(): boolean {
    return !!this.productScope.bannerPath;
  }

  get nextStep(): Step {
    for (const step of this.steps) {
      if (this.progress < step.target) {
        return step;
      }
    }
    return this.steps[this.steps.length - 1];
  }

  get maxPotentialReward(): number {
    return this.steps[this.steps.length - 1].reward;
  }

  get maxTarget(): number {
    return this.steps[this.steps.length - 1].target;
  }

  override getChallengeName(): string {
    return this.productScope.name;
  }
}

export class FrequencyChallenge extends ChallengeBase {
  declare public type: OfferType.FREQUENCY;
  target: number;
  rewardRatio: number;
  minAmountByTransaction: number;

  constructor(campaignId: number, challengeDTO: FrequencyChallengeDTO) {
    super(campaignId, challengeDTO);
    this.target = challengeDTO.target;
    this.rewardRatio = challengeDTO.reward_ratio;
    this.minAmountByTransaction = challengeDTO.min_amount_by_transaction;
  }

  get steps(): number[] {
    return Array.from({ length: this.target }, (_, i) => i + 1);
  }

  get isCompleted(): boolean {
    return this.isSelected && this.progress >= this.target;
  }
}

export class ReferralChallenge extends ChallengeBase {
  declare public type: OfferType.SPONSOR;
  steps: Step[];
  code: string;
  nbPendingSponsorships: number;

  constructor(campaignId: number, challengeDTO: ReferralChallengeDTO) {
    super(campaignId, challengeDTO);
    this.code = challengeDTO.code;
    this.steps = challengeDTO.steps;
    this.nbPendingSponsorships = challengeDTO.nb_pending_sponsorships;
  }
}

export class SelectToWinChallenge extends ChallengeBase {
  declare public type: OfferType.SELECT_TO_WIN;
  prize: number;

  constructor(campaignId: number, challengeDTO: SelectToWinChallengeDTO) {
    super(campaignId, challengeDTO);
    this.prize = challengeDTO.prize;
  }
}
